import { query, doc, collection, setDoc, getDocs, serverTimestamp, where, limit, deleteDoc, getDoc, updateDoc, getCountFromServer, onSnapshot, orderBy, runTransaction, Timestamp, type QueryDocumentSnapshot } from '@firebase/firestore'
import { type FormData } from '../models/FormData'
import { FirebaseDB } from '../config/firebase'
import {
  sendEmailAnniversarySignUp,
  sendEmailChristmasDinnerSignUp,
  sendEmailChristmasDinner2025SignUp,
  sendEmailChristmasDinner2026SignUp,
  sendEmailHookie,
  sendEmailRegistration
} from './mailerService'
import { type AnniversaryFormData } from '../models/AnniversaryFormData'
import { type HookieFormData } from '../models/HookieFormData'
import { type ChristmasDinnerFormData } from '../models/ChristmasDinnerFormData'
import { type ChristmasDinner2025FormData } from '../models/ChristmasDinner2025FormData'
import { type ChristmasDinner2026FormData } from '../models/ChristmasDinner2026FormData'
import config from '../config/config'

export interface FirebaseResponse<T> {
  success: boolean
  data?: T
  error?: string
}

export interface OneTimeToken {
  id: string
  used: boolean
  createdAt: any
}

// Función para validar DNI/NIE
export const validateDniNie = (dniNie: string): boolean => {
  // Eliminar espacios y convertir a mayúsculas
  const cleanDniNie = dniNie.trim().toUpperCase()

  // Verificar formato básico
  if (!/^[0-9XYZ][0-9]{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/i.test(cleanDniNie)) {
    return false
  }

  // Letras de control para DNI
  const dniLetters = 'TRWAGMYFPDXBNJZSQVHLCKE'

  // Si es DNI (empieza con número)
  if (/^[0-9]/.test(cleanDniNie)) {
    const number = parseInt(cleanDniNie.substring(0, 8), 10)
    const letter = cleanDniNie.charAt(8)
    const expectedLetter = dniLetters.charAt(number % 23)
    return letter === expectedLetter
  }

  // Si es NIE (empieza con X, Y, Z)
  if (/^[XYZ]/.test(cleanDniNie)) {
    let nieNumber = cleanDniNie.substring(1, 8)
    const letter = cleanDniNie.charAt(8)

    // Reemplazar la letra inicial por su número correspondiente
    if (cleanDniNie.charAt(0) === 'X') nieNumber = '0' + nieNumber
    else if (cleanDniNie.charAt(0) === 'Y') nieNumber = '1' + nieNumber
    else if (cleanDniNie.charAt(0) === 'Z') nieNumber = '2' + nieNumber

    const number = parseInt(nieNumber, 10)
    const expectedLetter = dniLetters.charAt(number % 23)
    return letter === expectedLetter
  }

  return false
}

export interface FormDataWithId extends FormData {
  id: string
}

export interface ChristmasDinner2026FormDataWithId extends ChristmasDinner2026FormData {
  id: string
}

export interface VotingParticipant {
  id: string
  name: string
}

export interface PublishedPairResult {
  rank: number
  names: [string, string]
  votes: number
}

export interface DinnerVotingConfig {
  isOpen: boolean
  requireLocation: boolean
  participants: VotingParticipant[]
  resultsPublished: boolean
  publishedResults: PublishedPairResult[]
}

export interface DinnerVote {
  // Id da persoa que votou: cada comensal ten como moito un voto
  id: string
  candidateIds: string[]
  deviceId: string | null
  createdAt: Date | null
}

export interface BlacklistedDni {
  id: string
  dni: string
  reason: string
  createdAt: any
}

export interface FreeDni {
  id: string
  dni: string
  reason: string
  createdAt: any
}

const collections = {
  renovation: 'renovation',
  renovation2425: 'renovation2425',
  renovation2526: 'renovation2526',
  renovation2627: 'renovation2627',
  anniversary: 'anniversary',
  hookie: 'hookie',
  christmasDinner: 'christmasDinner',
  christmasDinner2025: 'christmasDinner2025',
  christmasDinner2026: 'christmasDinner2026',
  christmasDinner2026Voting: 'christmasDinner2026Voting',
  christmasDinner2026Votes: 'christmasDinner2026Votes',
  oneTimeTokens: 'oneTimeTokens',
  dniBlacklist: 'dniBlacklist',
  dniFreeList: 'dniFreeList'
}

function getRenovationCollection (): string {
  const season = config.SEASON

  if (season === '2024-2025') {
    return collections.renovation2425
  } else if (season === '2025-2026') {
    return collections.renovation2526
  } else if (season === '2026-2027') {
    return collections.renovation2627
  } else {
    return collections.renovation
  }
}

const renovationCollections = [
  collections.renovation,
  collections.renovation2425,
  collections.renovation2526,
  collections.renovation2627
]

// Las temporadas que no son la actual están cerradas: ya no se escriben, así
// que se pueden cachear de forma persistente en el navegador
function isClosedSeasonCollection (collectionName: string): boolean {
  return renovationCollections.includes(collectionName) && collectionName !== getRenovationCollection()
}

function getPrevRenovationCollection (): string {
  const season = config.SEASON

  if (season === '2024-2025') {
    return collections.renovation
  } else if (season === '2025-2026') {
    return collections.renovation2425
  } else if (season === '2026-2027') {
    return collections.renovation2526
  } else {
    return collections.renovation2425
  }
}

export const sendRenovation = async (data: FormData): Promise<FirebaseResponse<string>> => {
  try {
    // Validar DNI principal
    const validDni = validateDniNie(data.dni)
    if (data.dni.trim() !== '' && !validDni) {
      return {
        success: false,
        error: 'El DNI/NIE introducido no es válido'
      }
    }

    // Validar DNI del tutor si existe
    if (data.tutorDni !== undefined && data.tutorDni !== '' && !validateDniNie(data.tutorDni)) {
      return {
        success: false,
        error: 'El DNI/NIE del tutor no es válido'
      }
    }

    const renovationCollection = getRenovationCollection()

    const cleanData = JSON.parse(JSON.stringify(data))
    cleanData.createdAt = serverTimestamp()

    // Recortamos espacios accidentales en los DNIs para que las búsquedas por
    // igualdad exacta (searchRenovation/checkDniExists) siempre los encuentren
    cleanData.dni = data.dni.trim()
    if (data.tutorDni !== undefined) {
      cleanData.tutorDni = data.tutorDni.trim()
    }

    const newDoc = doc(collection(FirebaseDB, renovationCollection))

    await setDoc(newDoc, cleanData)
    // La copia en localStorage no se toca: la próxima sincronización trae el
    // documento nuevo por su createdAt
    registrationsCache.delete(renovationCollection)
    if (cleanData.dni !== '') registeredDniCache.add(normalizeDni(cleanData.dni))
    if (data.isRenovation) hasAnyRenovationThisSeason = true

    await sendEmailRegistration(data)

    return {
      success: true,
      data: newDoc.id
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

// ─── Cachés de comprobaciones de DNI ─────────────────────────────────────────
// El formulario repite las mismas comprobaciones al salir del campo DNI (cada
// vez, aunque el DNI no cambie) y de nuevo al enviar. Se guardan en memoria
// durante la sesión solo los resultados que no pueden cambiar en ese tiempo (o
// que solo cambian desde el admin, que actualiza estas cachés al escribir).

const normalizeDni = (dni: string): string => dni.trim().toUpperCase()

// La temporada anterior está cerrada, así que una búsqueda da siempre el mismo resultado
const searchRenovationCache = new Map<string, FormData[] | null>()
const blacklistedDniCache = new Map<string, boolean>()
const freeDniCache = new Map<string, boolean>()
// Solo se cachean los positivos: un DNI ya registrado lo sigue estando, pero uno
// no registrado puede registrarse en otra pestaña y hay que comprobarlo al enviar
const registeredDniCache = new Set<string>()
// Una vez existe una renovación en la temporada, ya nunca vuelve a ser la primera
let hasAnyRenovationThisSeason = false

export const searchRenovation = async ({ dni, birthDate }: { dni: string, birthDate: string }): Promise<FirebaseResponse<FormData[] | null>> => {
  const cacheKey = `${normalizeDni(dni)}|${birthDate}`
  const cachedResult = searchRenovationCache.get(cacheKey)
  if (cachedResult !== undefined) {
    return cachedResult !== null
      ? { success: true, data: cachedResult }
      : { success: false, error: 'No such document!' }
  }

  try {
    const prevRenovationCollection = getPrevRenovationCollection()

    // Buscar en múltiples formatos para compatibilidad con datos existentes.
    // Algunos DNIs de la colección 2025-2026 quedaron persistidos con un
    // espacio al final, así que probamos también esa variante.
    // Todas las variantes se comprueban en una sola query con 'in' en vez de
    // una query por variante (Firestore factura 1 lectura mínima por query).
    const trimmedDni = dni.trim()
    const dniVariants = Array.from(new Set([
      trimmedDni, // DNI original
      trimmedDni.toLowerCase(), // DNI en minúsculas
      trimmedDni.toUpperCase(), // DNI en mayúsculas
      trimmedDni + ' ', // DNI original con espacio final
      trimmedDni.toLowerCase() + ' ', // DNI en minúsculas con espacio final
      trimmedDni.toUpperCase() + ' ' // DNI en mayúsculas con espacio final
    ]))

    const q = query(collection(FirebaseDB, prevRenovationCollection),
      where('dni', 'in', dniVariants),
      where('birthDate', '==', birthDate),
      limit(1)
    )

    const docSnap = await getDocs(q)

    if (!docSnap.empty) {
      const found = docSnap.docs.map(doc => doc.data()) as FormData[]
      searchRenovationCache.set(cacheKey, found)
      return {
        success: true,
        data: found
      }
    }

    // Si no se encontró ninguna variante
    searchRenovationCache.set(cacheKey, null)
    return {
      success: false,
      error: 'No such document!'
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const sendHookie = async (data: HookieFormData): Promise<FirebaseResponse<string>> => {
  try {
    data.createdAt = serverTimestamp()

    const newDoc = doc(collection(FirebaseDB, collections.hookie))

    await setDoc(newDoc, data)

    await sendEmailHookie(data)

    return {
      success: true,
      data: newDoc.id
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const sendAnniversarySignUp = async (data: AnniversaryFormData): Promise<FirebaseResponse<string>> => {
  try {
    data.createdAt = serverTimestamp()

    const newDoc = doc(collection(FirebaseDB, collections.anniversary))

    await setDoc(newDoc, data)

    await sendEmailAnniversarySignUp(data)

    return {
      success: true,
      data: newDoc.id
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const sendChristmasDinnerSignUp = async (data: ChristmasDinnerFormData): Promise<FirebaseResponse<string>> => {
  try {
    data.createdAt = serverTimestamp()

    const newDoc = doc(collection(FirebaseDB, collections.christmasDinner))

    await setDoc(newDoc, data)

    await sendEmailChristmasDinnerSignUp(data)

    return {
      success: true,
      data: newDoc.id
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const sendChristmasDinner2025SignUp = async (data: ChristmasDinner2025FormData): Promise<FirebaseResponse<string>> => {
  try {
    data.createdAt = serverTimestamp()

    const newDoc = doc(collection(FirebaseDB, collections.christmasDinner2025))

    await setDoc(newDoc, data)

    await sendEmailChristmasDinner2025SignUp(data)

    return {
      success: true,
      data: newDoc.id
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

// Función genérica para obtener todas las renovaciones de una colección específica
export const sendChristmasDinner2026SignUp = async (data: ChristmasDinner2026FormData): Promise<FirebaseResponse<string>> => {
  try {
    data.createdAt = serverTimestamp()

    const newDoc = doc(collection(FirebaseDB, collections.christmasDinner2026))

    await setDoc(newDoc, data)

    try {
      await sendEmailChristmasDinner2026SignUp(data)
    } catch (emailError: any) {
      // Sen o correo de confirmación a persoa vai reintentar, así que borramos o rexistro para non duplicalo
      await deleteDoc(newDoc)
      throw emailError
    }

    return {
      success: true,
      data: newDoc.id
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getAllRenovations = async (): Promise<FirebaseResponse<FormData[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.renovation))
    const docSnap = await getDocs(q)

    if (!docSnap.empty) {
      return {
        success: true,
        data: docSnap.docs.map(doc => doc.data()) as FormData[]
      }
    } else {
      return {
        success: false,
        error: 'No such document!'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getAllRenovations2425 = async (): Promise<FirebaseResponse<FormData[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.renovation2425))
    const docSnap = await getDocs(q)

    if (!docSnap.empty) {
      return {
        success: true,
        data: docSnap.docs.map(doc => doc.data()) as FormData[]
      }
    } else {
      return {
        success: false,
        error: 'No such document!'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getAllAnniversaryInscriptions = async (): Promise<FirebaseResponse<AnniversaryFormData[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.anniversary))
    const docSnap = await getDocs(q)

    if (!docSnap.empty) {
      return {
        success: true,
        data: docSnap.docs.map(doc => doc.data()) as AnniversaryFormData[]
      }
    } else {
      return {
        success: false,
        error: 'No such document!'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getAllChristmasDinnerInscriptions = async (): Promise<FirebaseResponse<ChristmasDinnerFormData[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.christmasDinner))
    const docSnap = await getDocs(q)

    if (!docSnap.empty) {
      return {
        success: true,
        data: docSnap.docs.map(doc => doc.data()) as ChristmasDinnerFormData[]
      }
    } else {
      return {
        success: false,
        error: 'No such document!'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getAllChristmasDinner2025Inscriptions = async (): Promise<FirebaseResponse<ChristmasDinner2025FormData[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.christmasDinner2025))
    const docSnap = await getDocs(q)

    if (!docSnap.empty) {
      return {
        success: true,
        data: docSnap.docs.map(doc => doc.data()) as ChristmasDinner2025FormData[]
      }
    } else {
      return {
        success: false,
        error: 'No such document!'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getAllChristmasDinner2026Inscriptions = async (): Promise<FirebaseResponse<ChristmasDinner2026FormData[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.christmasDinner2026))
    const docSnap = await getDocs(q)

    if (!docSnap.empty) {
      return {
        success: true,
        data: docSnap.docs.map(doc => doc.data()) as ChristmasDinner2026FormData[]
      }
    } else {
      return {
        success: false,
        error: 'No such document!'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getAllHookiesRegistrations = async (): Promise<FirebaseResponse<HookieFormData[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.hookie))
    const docSnap = await getDocs(q)

    if (!docSnap.empty) {
      return {
        success: true,
        data: docSnap.docs.map(doc => doc.data()) as HookieFormData[]
      }
    } else {
      return {
        success: false,
        error: 'No such document!'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getNumberOfSignUps = async (): Promise<number> => {
  try {
    // Se cuenta como "más de 16 años" tanto quien tiene birthDate anterior a
    // hace 16 años (mismo cálculo simplificado que antes, solo por año) como
    // quien no tiene birthDate guardado. Firestore no permite filtrar por
    // "campo inexistente" directamente, así que se calcula como
    // total - (los que sí tienen birthDate y son menores), en vez de traer
    // todos los documentos y filtrar en el cliente.
    const currentYear = new Date().getFullYear()
    const cutoffDate = `${currentYear - 16}-01-01`

    const anniversaryCollection = collection(FirebaseDB, collections.anniversary)
    const [totalSnapshot, minorsWithBirthDateSnapshot] = await Promise.all([
      getCountFromServer(anniversaryCollection),
      getCountFromServer(query(anniversaryCollection, where('birthDate', '>=', cutoffDate)))
    ])

    return totalSnapshot.data().count - minorsWithBirthDateSnapshot.data().count
  } catch (error: any) {
    return 0
  }
}

export const getNumberOfSignUpsChristmasDinner = async (): Promise<number> => {
  try {
    const snapshot = await getCountFromServer(collection(FirebaseDB, collections.christmasDinner))

    return snapshot.data().count
  } catch (error: any) {
    return 0
  }
}

export const getNumberOfSignUpsChristmasDinner2025 = async (): Promise<number> => {
  try {
    const snapshot = await getCountFromServer(collection(FirebaseDB, collections.christmasDinner2025))

    return snapshot.data().count
  } catch (error: any) {
    return 0
  }
}

export const getNumberOfSignUpsChristmasDinner2026 = async (): Promise<number> => {
  try {
    const snapshot = await getCountFromServer(collection(FirebaseDB, collections.christmasDinner2026))

    return snapshot.data().count
  } catch (error: any) {
    return 0
  }
}

// La cabecera HTTP Date la pone nuestro propio hosting, así que sirve igual que
// Firestore como hora de referencia que el usuario no controla, pero sin gastar
// lecturas ni escrituras. Es una petición al mismo origen (también dentro del
// iframe), por lo que la cabecera es legible sin CORS.
const getServerDateFromHttpHeader = async (): Promise<Date | null> => {
  try {
    const response = await fetch(`${window.location.origin}/?serverTime=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store'
    })
    const dateHeader = response.headers.get('Date')
    if (dateHeader === null) return null
    const serverDate = new Date(dateHeader)
    return isNaN(serverDate.getTime()) ? null : serverDate
  } catch {
    return null
  }
}

export const getServerTimestamp = async (): Promise<FirebaseResponse<Date>> => {
  const httpServerDate = await getServerDateFromHttpHeader()
  if (httpServerDate !== null) {
    return {
      success: true,
      data: httpServerDate
    }
  }

  // Si el hosting no devuelve la cabecera, recurrimos a Firestore (1 escritura + 1 lectura)
  try {
    // Reutilizamos siempre el mismo documento para evitar generar un documento
    // (y una eliminación) nuevo en cada llamada
    const tempDocRef = doc(FirebaseDB, 'temp', 'serverTimeSync')
    await setDoc(tempDocRef, {
      timestamp: serverTimestamp()
    })

    const docSnapshot = await getDoc(tempDocRef)

    if (docSnapshot.exists()) {
      const data = docSnapshot.data()
      const serverDate = data.timestamp.toDate()

      return {
        success: true,
        data: serverDate
      }
    } else {
      return {
        success: false,
        error: 'No se pudo obtener la fecha del servidor'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const isFirstRegistration = async (): Promise<FirebaseResponse<boolean>> => {
  if (hasAnyRenovationThisSeason) {
    return { success: true, data: false }
  }

  try {
    const renovationCollection = getRenovationCollection()
    const q = query(collection(FirebaseDB, renovationCollection), where('isRenovation', '==', true), limit(1))
    const docSnap = await getDocs(q)
    hasAnyRenovationThisSeason = !docSnap.empty

    return {
      success: true,
      data: docSnap.empty
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const checkDniExists = async (dni: string): Promise<FirebaseResponse<boolean>> => {
  if (registeredDniCache.has(normalizeDni(dni))) {
    return { success: true, data: true }
  }

  try {
    const currentRenovationCollection = getRenovationCollection()

    // Buscar en múltiples formatos para compatibilidad con datos existentes,
    // en una sola query (Firestore factura 1 lectura mínima por query, así
    // que un único 'in' con todas las variantes es mucho más barato que
    // lanzar una query por variante)
    const dniVariants = Array.from(new Set([
      dni.trim(), // DNI original
      dni.trim().toLowerCase(), // DNI en minúsculas
      dni.trim().toUpperCase() // DNI en mayúsculas
    ]))

    const q = query(collection(FirebaseDB, currentRenovationCollection),
      where('dni', 'in', dniVariants),
      limit(1)
    )

    const docSnap = await getDocs(q)
    if (!docSnap.empty) registeredDniCache.add(normalizeDni(dni))

    return {
      success: true,
      data: !docSnap.empty
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const validateOneTimeToken = async (token: string): Promise<FirebaseResponse<boolean>> => {
  try {
    const tokenDocRef = doc(FirebaseDB, collections.oneTimeTokens, token)
    const tokenDoc = await getDoc(tokenDocRef)

    if (tokenDoc.exists() && tokenDoc.data().used === false) {
      return {
        success: true,
        data: true
      }
    } else {
      return {
        success: true,
        data: false
      }
    }
  } catch (error: any) {
    console.error('Error validating token:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

export const invalidateOneTimeToken = async (token: string): Promise<FirebaseResponse<null>> => {
  try {
    const tokenDocRef = doc(FirebaseDB, collections.oneTimeTokens, token)
    await updateDoc(tokenDocRef, {
      used: true
    })
    return {
      success: true,
      data: null
    }
  } catch (error: any) {
    console.error('Error invalidating token:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

export const generateOneTimeToken = async (): Promise<FirebaseResponse<string>> => {
  try {
    const newTokenRef = doc(collection(FirebaseDB, collections.oneTimeTokens))
    await setDoc(newTokenRef, {
      used: false,
      createdAt: serverTimestamp()
    })
    return {
      success: true,
      data: newTokenRef.id
    }
  } catch (error: any) {
    console.error('Error generating one-time token:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

export const RECENT_ONE_TIME_TOKENS_LIMIT = 30

// En la página de tokens solo interesan los disponibles (para copiarlos o
// revocarlos) y los más recientes. Los usados se acumulan cada temporada, así
// que no se leen todos en cada visita; los totales salen de getOneTimeTokenStats.
export const getVisibleOneTimeTokens = async (): Promise<FirebaseResponse<OneTimeToken[]>> => {
  try {
    const tokensCollection = collection(FirebaseDB, collections.oneTimeTokens)
    const [availableSnapshot, recentSnapshot] = await Promise.all([
      getDocs(query(tokensCollection, where('used', '==', false))),
      getDocs(query(tokensCollection, orderBy('createdAt', 'desc'), limit(RECENT_ONE_TIME_TOKENS_LIMIT)))
    ])

    const tokensById = new Map<string, OneTimeToken>()
    for (const d of [...availableSnapshot.docs, ...recentSnapshot.docs]) {
      const data = d.data()
      tokensById.set(d.id, { id: d.id, used: data.used, createdAt: data.createdAt })
    }

    return {
      success: true,
      data: Array.from(tokensById.values())
    }
  } catch (error: any) {
    console.error('Error fetching one-time tokens:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

export interface OneTimeTokenStats {
  available: number
  used: number
}

export const getOneTimeTokenStats = async (): Promise<OneTimeTokenStats> => {
  try {
    const tokensCollection = collection(FirebaseDB, collections.oneTimeTokens)
    const [totalSnapshot, usedSnapshot] = await Promise.all([
      getCountFromServer(tokensCollection),
      getCountFromServer(query(tokensCollection, where('used', '==', true)))
    ])
    const total = totalSnapshot.data().count
    const used = usedSnapshot.data().count
    return { available: total - used, used }
  } catch (error: any) {
    return { available: 0, used: 0 }
  }
}

export const revokeOneTimeToken = async (token: string): Promise<FirebaseResponse<null>> => {
  try {
    const tokenDocRef = doc(FirebaseDB, collections.oneTimeTokens, token)
    await deleteDoc(tokenDocRef)
    return {
      success: true,
      data: null
    }
  } catch (error: any) {
    console.error('Error revoking token:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

export const checkDniBlacklisted = async (dni: string): Promise<FirebaseResponse<boolean>> => {
  const normalizedDni = normalizeDni(dni)
  const cached = blacklistedDniCache.get(normalizedDni)
  if (cached !== undefined) {
    return { success: true, data: cached }
  }

  try {
    const dniDocRef = doc(FirebaseDB, collections.dniBlacklist, normalizedDni)
    const dniDoc = await getDoc(dniDocRef)
    blacklistedDniCache.set(normalizedDni, dniDoc.exists())
    return {
      success: true,
      data: dniDoc.exists()
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const addDniToBlacklist = async (dni: string, reason: string = ''): Promise<FirebaseResponse<null>> => {
  try {
    const normalizedDni = dni.trim().toUpperCase()
    const dniDocRef = doc(FirebaseDB, collections.dniBlacklist, normalizedDni)
    await setDoc(dniDocRef, {
      dni: normalizedDni,
      reason,
      createdAt: serverTimestamp()
    })
    blacklistedDniCache.set(normalizedDni, true)
    return {
      success: true,
      data: null
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const removeDniFromBlacklist = async (dni: string): Promise<FirebaseResponse<null>> => {
  try {
    const normalizedDni = dni.trim().toUpperCase()
    const dniDocRef = doc(FirebaseDB, collections.dniBlacklist, normalizedDni)
    await deleteDoc(dniDocRef)
    blacklistedDniCache.set(normalizedDni, false)
    return {
      success: true,
      data: null
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

// ─── Caché de rexistros ──────────────────────────────────────────────────────
// Descargar una temporada completa cuesta una lectura por documento (cientos).
// Para no hacerlo en cada visita se cachea en dos niveles:
//  - En memoria durante REGISTRATIONS_CACHE_TTL_MS: navegar entre páginas de
//    admin o recargar un panel no lee nada.
//  - En localStorage, como base para sincronizar solo los cambios: se piden los
//    documentos creados (createdAt) o editados (updatedAt) después del último
//    que tenemos, y un conteo confirma que la copia sigue completa. Son ~3
//    lecturas en vez de una por documento. Si el conteo no cuadra (p. ej. alguien
//    borró un documento) o la copia tiene más de FULL_SYNC_MAX_AGE_MS, se
//    descarga entera. Las temporadas cerradas ya no cambian, así que solo se
//    validan con el conteo y nunca caducan.
// Las escrituras hechas desde esta app parchean ambos niveles.

const REGISTRATIONS_CACHE_TTL_MS = 2 * 60 * 1000
const FULL_SYNC_MAX_AGE_MS = 24 * 60 * 60 * 1000
// Solape al pedir cambios, para no perder escrituras con timestamps casi
// simultáneos al último conocido (los repetidos se fusionan por id)
const SYNC_OVERLAP_MS = 60 * 1000
const REGISTRATIONS_STORAGE_PREFIX = 'registrationsCache:v2:'
const LEGACY_REGISTRATIONS_STORAGE_PREFIX = 'registrationsCache:v1:'

// 'cache': memoria si está vigente; 'sync': pide solo los cambios al servidor;
// 'full': descarga la colección entera
export type RegistrationsFreshness = 'cache' | 'sync' | 'full'

interface CachedRegistrations {
  records: FormDataWithId[]
  fetchedAt: number
}

interface StoredRegistrations {
  records: FormDataWithId[]
  fullSyncAt: number
}

const registrationsCache = new Map<string, CachedRegistrations>()

// Los Timestamp de Firestore no sobreviven a JSON.stringify, así que se
// guardan como { seconds, nanoseconds } marcados y se reconstruyen al leer
const TIMESTAMP_MARKER = '__firestoreTimestamp'

const serializeRecords = (records: FormDataWithId[]): Array<Record<string, any>> =>
  records.map(record => Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key,
      value instanceof Timestamp
        ? { [TIMESTAMP_MARKER]: true, seconds: value.seconds, nanoseconds: value.nanoseconds }
        : value
    ])
  ))

const deserializeRecords = (records: Array<Record<string, any>>): FormDataWithId[] =>
  records.map(record => Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key,
      value?.[TIMESTAMP_MARKER] === true ? new Timestamp(value.seconds, value.nanoseconds) : value
    ])
  )) as FormDataWithId[]

const readStoredRegistrations = (collectionName: string): StoredRegistrations | null => {
  try {
    const raw = localStorage.getItem(REGISTRATIONS_STORAGE_PREFIX + collectionName)
    if (raw === null) return null
    const parsed = JSON.parse(raw)
    return { records: deserializeRecords(parsed.records), fullSyncAt: parsed.fullSyncAt }
  } catch {
    return null
  }
}

const writeStoredRegistrations = (collectionName: string, stored: StoredRegistrations): void => {
  try {
    localStorage.removeItem(LEGACY_REGISTRATIONS_STORAGE_PREFIX + collectionName)
    localStorage.setItem(REGISTRATIONS_STORAGE_PREFIX + collectionName, JSON.stringify({
      records: serializeRecords(stored.records),
      fullSyncAt: stored.fullSyncAt
    }))
  } catch {
    // Sin localStorage o sin espacio: seguimos solo con la caché en memoria
  }
}

function patchCachedRegistrations (collectionName: string, patch: (records: FormDataWithId[]) => FormDataWithId[]): void {
  const cached = registrationsCache.get(collectionName)
  if (cached !== undefined) {
    registrationsCache.set(collectionName, { ...cached, records: patch(cached.records) })
  }
  const stored = readStoredRegistrations(collectionName)
  if (stored !== null) {
    writeStoredRegistrations(collectionName, { ...stored, records: patch(stored.records) })
  }
}

const toRegistration = (d: QueryDocumentSnapshot): FormDataWithId => ({
  id: d.id,
  ...(d.data() as FormData)
})

const getLatestWriteMillis = (records: FormDataWithId[]): number =>
  records.reduce((latest, record) => Math.max(
    latest,
    ...[record.createdAt, record.updatedAt]
      .filter((value): value is Timestamp => value instanceof Timestamp)
      .map(value => value.toMillis())
  ), 0)

// Devuelve la copia local actualizada, o null si no se puede confiar en ella
const syncStoredRegistrations = async (collectionName: string, stored: StoredRegistrations): Promise<FormDataWithId[] | null> => {
  const registrationsCollection = collection(FirebaseDB, collectionName)

  if (isClosedSeasonCollection(collectionName)) {
    const countSnapshot = await getCountFromServer(registrationsCollection)
    return countSnapshot.data().count === stored.records.length ? stored.records : null
  }

  const since = new Date(Math.max(0, getLatestWriteMillis(stored.records) - SYNC_OVERLAP_MS))
  const [countSnapshot, createdSnapshot, updatedSnapshot] = await Promise.all([
    getCountFromServer(registrationsCollection),
    getDocs(query(registrationsCollection, where('createdAt', '>', since))),
    getDocs(query(registrationsCollection, where('updatedAt', '>', since)))
  ])

  const recordsById = new Map(stored.records.map(record => [record.id, record]))
  for (const d of [...createdSnapshot.docs, ...updatedSnapshot.docs]) {
    recordsById.set(d.id, toRegistration(d))
  }
  const records = Array.from(recordsById.values())
  return records.length === countSnapshot.data().count ? records : null
}

const loadRegistrations = async (collectionName: string, freshness: RegistrationsFreshness): Promise<FormDataWithId[]> => {
  const stored = freshness === 'full' ? null : readStoredRegistrations(collectionName)
  const isExpired = stored !== null && !isClosedSeasonCollection(collectionName) &&
    Date.now() - stored.fullSyncAt >= FULL_SYNC_MAX_AGE_MS

  if (stored !== null && !isExpired) {
    const synced = await syncStoredRegistrations(collectionName, stored)
    if (synced !== null) {
      if (synced !== stored.records) {
        writeStoredRegistrations(collectionName, { records: synced, fullSyncAt: stored.fullSyncAt })
      }
      return synced
    }
  }

  const querySnapshot = await getDocs(query(collection(FirebaseDB, collectionName)))
  const records = querySnapshot.docs.map(toRegistration)
  writeStoredRegistrations(collectionName, { records, fullSyncAt: Date.now() })
  return records
}

export const getRegistrationsByCollection = async (
  collectionName: string,
  freshness: RegistrationsFreshness = 'cache'
): Promise<FirebaseResponse<FormDataWithId[]>> => {
  try {
    const cached = registrationsCache.get(collectionName)
    if (freshness === 'cache' && cached !== undefined && Date.now() - cached.fetchedAt < REGISTRATIONS_CACHE_TTL_MS) {
      // Copia del array: los llamadores lo ordenan in situ
      return { success: true, data: [...cached.records] }
    }

    const records = await loadRegistrations(collectionName, freshness)
    registrationsCache.set(collectionName, { records, fetchedAt: Date.now() })
    return { success: true, data: [...records] }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const getAllRenovations2627 = async (): Promise<FirebaseResponse<FormDataWithId[]>> => {
  return await getRegistrationsByCollection(collections.renovation2627)
}

export interface SeasonSummaryCounts {
  total: number
  renovations: number
}

// Para temporadas ya cerradas (que el dashboard solo muestra como totales
// históricos) no hace falta traer todos los documentos: basta con contar en
// el servidor.
export const getSeasonSummaryCounts = async (collectionName: string): Promise<SeasonSummaryCounts> => {
  try {
    const seasonCollection = collection(FirebaseDB, collectionName)
    const [totalSnapshot, renovationsSnapshot] = await Promise.all([
      getCountFromServer(seasonCollection),
      getCountFromServer(query(seasonCollection, where('isRenovation', '==', true)))
    ])
    return {
      total: totalSnapshot.data().count,
      renovations: renovationsSnapshot.data().count
    }
  } catch (error: any) {
    return { total: 0, renovations: 0 }
  }
}

export const updateRenovation2627 = async (id: string, data: Partial<FormData>): Promise<FirebaseResponse<null>> => {
  try {
    const docRef = doc(FirebaseDB, collections.renovation2627, id)
    // updatedAt permite a otras sesiones traer solo los documentos editados
    await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() })
    patchCachedRegistrations(collections.renovation2627, records =>
      records.map(r => (r.id === id ? { ...r, ...data } : r))
    )
    return { success: true, data: null }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const deleteRenovation2627 = async (id: string): Promise<FirebaseResponse<null>> => {
  try {
    const docRef = doc(FirebaseDB, collections.renovation2627, id)
    await deleteDoc(docRef)
    patchCachedRegistrations(collections.renovation2627, records => records.filter(r => r.id !== id))
    return { success: true, data: null }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const getChristmasDinner2026Registrations = async (): Promise<FirebaseResponse<ChristmasDinner2026FormDataWithId[]>> => {
  try {
    const querySnapshot = await getDocs(query(collection(FirebaseDB, collections.christmasDinner2026)))
    const records: ChristmasDinner2026FormDataWithId[] = querySnapshot.docs.map(d => ({
      id: d.id,
      ...(d.data() as ChristmasDinner2026FormData)
    }))
    return { success: true, data: records }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const updateChristmasDinner2026Registration = async (id: string, data: Partial<ChristmasDinner2026FormData>): Promise<FirebaseResponse<null>> => {
  try {
    const docRef = doc(FirebaseDB, collections.christmasDinner2026, id)
    await updateDoc(docRef, data as Record<string, any>)
    return { success: true, data: null }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const deleteChristmasDinner2026Registration = async (id: string): Promise<FirebaseResponse<null>> => {
  try {
    const docRef = doc(FirebaseDB, collections.christmasDinner2026, id)
    await deleteDoc(docRef)
    return { success: true, data: null }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const getAllBlacklistedDnis = async (): Promise<FirebaseResponse<BlacklistedDni[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.dniBlacklist))
    const querySnapshot = await getDocs(q)
    const dnis: BlacklistedDni[] = querySnapshot.docs.map(d => ({
      id: d.id,
      ...(d.data() as Omit<BlacklistedDni, 'id'>)
    }))
    return {
      success: true,
      data: dnis
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getBlacklistedDnisCount = async (): Promise<number> => {
  try {
    const snapshot = await getCountFromServer(collection(FirebaseDB, collections.dniBlacklist))
    return snapshot.data().count
  } catch (error: any) {
    return 0
  }
}

export const checkDniFree = async (dni: string): Promise<FirebaseResponse<boolean>> => {
  const normalizedDni = normalizeDni(dni)
  const cached = freeDniCache.get(normalizedDni)
  if (cached !== undefined) {
    return { success: true, data: cached }
  }

  try {
    const dniDocRef = doc(FirebaseDB, collections.dniFreeList, normalizedDni)
    const dniDoc = await getDoc(dniDocRef)
    freeDniCache.set(normalizedDni, dniDoc.exists())
    return {
      success: true,
      data: dniDoc.exists()
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const addDniToFreeList = async (dni: string, reason: string = ''): Promise<FirebaseResponse<null>> => {
  try {
    const normalizedDni = dni.trim().toUpperCase()
    const dniDocRef = doc(FirebaseDB, collections.dniFreeList, normalizedDni)
    await setDoc(dniDocRef, {
      dni: normalizedDni,
      reason,
      createdAt: serverTimestamp()
    })
    freeDniCache.set(normalizedDni, true)
    return {
      success: true,
      data: null
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const removeDniFromFreeList = async (dni: string): Promise<FirebaseResponse<null>> => {
  try {
    const normalizedDni = dni.trim().toUpperCase()
    const dniDocRef = doc(FirebaseDB, collections.dniFreeList, normalizedDni)
    await deleteDoc(dniDocRef)
    freeDniCache.set(normalizedDni, false)
    return {
      success: true,
      data: null
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getAllFreeDnis = async (): Promise<FirebaseResponse<FreeDni[]>> => {
  try {
    const q = query(collection(FirebaseDB, collections.dniFreeList))
    const querySnapshot = await getDocs(q)
    const dnis: FreeDni[] = querySnapshot.docs.map(d => ({
      id: d.id,
      ...(d.data() as Omit<FreeDni, 'id'>)
    }))
    return {
      success: true,
      data: dnis
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message
    }
  }
}

export const getFreeDnisCount = async (): Promise<number> => {
  try {
    const snapshot = await getCountFromServer(collection(FirebaseDB, collections.dniFreeList))
    return snapshot.data().count
  } catch (error: any) {
    return 0
  }
}

// ─── Votación da cea de Nadal 2026 ───────────────────────────────────────────
// A páxina pública só le o doc de configuración, que garda unicamente os nomes
// dos participantes (copiados dos rexistros ao abrir a votación) para non expor
// correos nin teléfonos. Cada comensal vota unha vez: o id do voto é o do votante.
// O dispositivo gárdase para impedir que o mesmo móbil vote máis dunha vez.

const VOTING_CONFIG_DOC_ID = 'config'

const toVotingConfig = (data: Record<string, any> | undefined): DinnerVotingConfig => ({
  isOpen: data?.isOpen === true,
  // Activada por defecto: só se desactiva se o admin o fai de forma explícita
  requireLocation: data?.requireLocation !== false,
  participants: (data?.participants ?? []) as VotingParticipant[],
  resultsPublished: data?.resultsPublished === true,
  publishedResults: (data?.publishedResults ?? []) as PublishedPairResult[]
})

export const getDinnerVotingConfig = async (): Promise<FirebaseResponse<DinnerVotingConfig>> => {
  try {
    const snapshot = await getDoc(doc(FirebaseDB, collections.christmasDinner2026Voting, VOTING_CONFIG_DOC_ID))
    return { success: true, data: toVotingConfig(snapshot.data()) }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

// Escoita en directo o doc de configuración (un só doc pequeno) para que a páxina pública
// reaccione ao abrir, pechar ou publicar sen recargar
export const subscribeDinnerVotingConfig = (
  onChange: (config: DinnerVotingConfig) => void,
  onError: (error: string) => void
): (() => void) =>
  onSnapshot(
    doc(FirebaseDB, collections.christmasDinner2026Voting, VOTING_CONFIG_DOC_ID),
    snapshot => { onChange(toVotingConfig(snapshot.data())) },
    error => { onError(error.message) }
  )

export const setDinnerVotingOpen = async (isOpen: boolean): Promise<FirebaseResponse<DinnerVotingConfig>> => {
  try {
    const configRef = doc(FirebaseDB, collections.christmasDinner2026Voting, VOTING_CONFIG_DOC_ID)
    if (!isOpen) {
      await setDoc(configRef, { isOpen: false }, { merge: true })
      return await getDinnerVotingConfig()
    }

    const registrations = await getChristmasDinner2026Registrations()
    if (!registrations.success || registrations.data === undefined) {
      return { success: false, error: registrations.error ?? 'Non se puideron cargar os participantes.' }
    }
    const participants: VotingParticipant[] = registrations.data
      .map(r => ({ id: r.id, name: `${r.name} ${r.lastName}`.trim() }))
      .sort((a, b) => a.name.localeCompare(b.name, 'gl'))
    // Reabrir oculta os resultados publicados, que quedarían desfasados
    await setDoc(configRef, {
      isOpen: true,
      participants,
      resultsPublished: false,
      publishedResults: [],
      openedAt: serverTimestamp()
    }, { merge: true })
    return await getDinnerVotingConfig()
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const setDinnerRequireLocation = async (requireLocation: boolean): Promise<FirebaseResponse<DinnerVotingConfig>> => {
  try {
    await setDoc(
      doc(FirebaseDB, collections.christmasDinner2026Voting, VOTING_CONFIG_DOC_ID),
      { requireLocation },
      { merge: true }
    )
    return await getDinnerVotingConfig()
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const setDinnerResultsPublished = async (
  publishedResults: PublishedPairResult[] | null
): Promise<FirebaseResponse<DinnerVotingConfig>> => {
  try {
    await setDoc(
      doc(FirebaseDB, collections.christmasDinner2026Voting, VOTING_CONFIG_DOC_ID),
      { resultsPublished: publishedResults !== null, publishedResults: publishedResults ?? [] },
      { merge: true }
    )
    return await getDinnerVotingConfig()
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const hasDinnerVoted = async (voterId: string): Promise<FirebaseResponse<boolean>> => {
  try {
    const snapshot = await getDoc(doc(FirebaseDB, collections.christmasDinner2026Votes, voterId))
    return { success: true, data: snapshot.exists() }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

// Id da persoa que votou desde este dispositivo, ou null se non hai ningún voto.
// Consúltase sempre en Firestore (non nunha marca do navegador) para que, ao borrar o voto, o dispositivo quede libre.
export const getDinnerVoterOfDevice = async (deviceId: string): Promise<FirebaseResponse<string | null>> => {
  try {
    const snapshot = await getDocs(query(
      collection(FirebaseDB, collections.christmasDinner2026Votes),
      where('deviceId', '==', deviceId),
      limit(1)
    ))
    return { success: true, data: snapshot.empty ? null : snapshot.docs[0].id }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export type CastVoteError = 'closed' | 'already-voted' | 'device-already-voted' | 'invalid' | 'error'

export const castDinnerVote = async (
  voterId: string,
  candidateIds: string[],
  deviceId: string
): Promise<FirebaseResponse<null> & { reason?: CastVoteError }> => {
  try {
    const config = await getDinnerVotingConfig()
    if (!config.success || config.data === undefined) return { success: false, reason: 'error', error: config.error }
    if (!config.data.isOpen) return { success: false, reason: 'closed' }

    const knownIds = new Set(config.data.participants.map(p => p.id))
    if (
      !knownIds.has(voterId) ||
      candidateIds.length !== 2 ||
      candidateIds[0] === candidateIds[1] ||
      !candidateIds.every(id => knownIds.has(id))
    ) {
      return { success: false, reason: 'invalid' }
    }

    // Un dispositivo, un voto (con calquera nome)
    const deviceVoter = await getDinnerVoterOfDevice(deviceId)
    if (deviceVoter.success && deviceVoter.data != null) return { success: false, reason: 'device-already-voted' }

    // En transacción: se dúas persoas escollen o mesmo nome á vez, só entra un voto
    const voteRef = doc(FirebaseDB, collections.christmasDinner2026Votes, voterId)
    const alreadyVoted = await runTransaction(FirebaseDB, async transaction => {
      if ((await transaction.get(voteRef)).exists()) return true
      transaction.set(voteRef, { candidateIds: [...candidateIds].sort(), deviceId, createdAt: serverTimestamp() })
      return false
    })
    if (alreadyVoted) return { success: false, reason: 'already-voted' }
    return { success: true, data: null }
  } catch (error: any) {
    return { success: false, reason: 'error', error: error.message }
  }
}

export const getDinnerVotes = async (): Promise<FirebaseResponse<DinnerVote[]>> => {
  try {
    const snapshot = await getDocs(collection(FirebaseDB, collections.christmasDinner2026Votes))
    return {
      success: true,
      data: snapshot.docs.map(d => ({
        id: d.id,
        candidateIds: (d.data().candidateIds ?? []) as string[],
        deviceId: (d.data().deviceId ?? null) as string | null,
        createdAt: d.data().createdAt?.toDate?.() ?? null
      }))
    }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const deleteDinnerVote = async (voteId: string): Promise<FirebaseResponse<null>> => {
  try {
    await deleteDoc(doc(FirebaseDB, collections.christmasDinner2026Votes, voteId))
    return { success: true, data: null }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}

export const resetDinnerVotes = async (): Promise<FirebaseResponse<null>> => {
  try {
    const snapshot = await getDocs(collection(FirebaseDB, collections.christmasDinner2026Votes))
    await Promise.all(snapshot.docs.map(async d => { await deleteDoc(d.ref) }))
    return { success: true, data: null }
  } catch (error: any) {
    return { success: false, error: error.message }
  }
}
